import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api";
import { useSettings } from "@/contexts/SettingsContext";
import { Section, Overline } from "@/components/Primitives";
import { Send, Check, MessageCircle, Phone } from "lucide-react";

export default function Events() {
  const { t, i18n } = useTranslation();
  const { settings: s } = useSettings();
  const types = t("events_page.event_types", { returnObjects: true }) || [];
  const [form, setForm] = useState({ name: "", phone: "", email: "", event_type: types[0] || "Autre", date: "", guests: 10, message: "" });
  const [status, setStatus] = useState({ loading: false, ok: false });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: false });
    try {
      await api.post("/events", { ...form, guests: Number(form.guests), language: i18n.language });
      setStatus({ loading: false, ok: true });
      setForm({ name: "", phone: "", email: "", event_type: types[0] || "Autre", date: "", guests: 10, message: "" });
    } catch { setStatus({ loading: false, ok: false }); }
  };
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div data-testid="events-page" className="pt-32">
      <Section testId="events-header">
        <Overline>{t("events_page.overline")}</Overline>
        <h1 className="font-serif-luxe text-5xl sm:text-6xl font-light tracking-tight text-[#F8F5EF] mt-3 mb-6">{t("events_page.title")}</h1>
        <p className="max-w-3xl text-[#F8F5EF]/70 leading-relaxed">{t("events_page.intro")}</p>
      </Section>

      <Section testId="events-form" className="!pt-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <form onSubmit={submit} data-testid="events-form-block" className="lg:col-span-7 luxe-card p-8 lg:p-10 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <label className="overline mb-2 block">{t("events_page.form_name")}</label>
                <input data-testid="evt-name" required value={form.name} onChange={e => upd("name", e.target.value)} className="input-luxe" />
              </div>
              <div>
                <label className="overline mb-2 block">{t("events_page.phone")}</label>
                <input data-testid="evt-phone" required value={form.phone} onChange={e => upd("phone", e.target.value)} className="input-luxe" />
              </div>
              <div>
                <label className="overline mb-2 block">{t("events_page.email")}</label>
                <input data-testid="evt-email" required type="email" value={form.email} onChange={e => upd("email", e.target.value)} className="input-luxe" />
              </div>
              <div>
                <label className="overline mb-2 block">{t("events_page.event_type")}</label>
                <select data-testid="evt-type" value={form.event_type} onChange={e => upd("event_type", e.target.value)} className="input-luxe">
                  {types.map(tp => <option key={tp} value={tp} className="bg-[#1A1A1A]">{tp}</option>)}
                </select>
              </div>
              <div>
                <label className="overline mb-2 block">{t("events_page.date")}</label>
                <input data-testid="evt-date" type="date" value={form.date} onChange={e => upd("date", e.target.value)} className="input-luxe" />
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-2 block">{t("events_page.guests")}</label>
                <input data-testid="evt-guests" required type="number" min="1" max="500" value={form.guests} onChange={e => upd("guests", e.target.value)} className="input-luxe" />
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-2 block">{t("events_page.message")}</label>
                <textarea data-testid="evt-message" rows={4} value={form.message} onChange={e => upd("message", e.target.value)} className="input-luxe" />
              </div>
            </div>
            <button data-testid="evt-submit" type="submit" disabled={status.loading} className="btn-gold disabled:opacity-50">
              {t("events_page.submit")} <Send size={14} />
            </button>
            {status.ok && (
              <div data-testid="evt-success" className="flex items-center gap-2 text-emerald-400 text-sm"><Check size={16} /> {t("events_page.success")}</div>
            )}
          </form>

          <aside className="lg:col-span-5 space-y-3">
            {s && (
              <>
                <a data-testid="evt-call" href={`tel:${s.phone?.replace(/\s/g, "")}`} className="btn-ghost-gold w-full justify-between">
                  <span className="flex items-center gap-3"><Phone size={16} /> {t("mobile_bar.call")}</span>
                  <span className="text-[#D4AF37]">{s.phone}</span>
                </a>
                <a data-testid="evt-whatsapp" href={`https://wa.me/${s.whatsapp_raw}`} target="_blank" rel="noreferrer" className="btn-ghost-gold w-full justify-between">
                  <span className="flex items-center gap-3"><MessageCircle size={16} /> WhatsApp</span>
                  <span className="text-[#D4AF37]">{s.whatsapp}</span>
                </a>
              </>
            )}
          </aside>
        </div>
      </Section>
    </div>
  );
}
